import { useEffect } from 'react';
import type { AccentId, Density, SidebarStyle, ThemeMode } from '@mailpilot/shared';
import { useMediaQuery } from './useBrowser';

const THEME_KEY = 'mp:theme';
const LOOK_KEY = 'mp:look';

interface Look {
  accent?: AccentId;
  sidebarStyle?: SidebarStyle;
  density?: Density;
}

function remember(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

/** Apply data-accent / data-sidebar / data-density on <html> (also called before first paint from main.tsx). */
export function applyLook(look: Look) {
  const el = document.documentElement;
  el.dataset.accent = look.accent ?? 'ocean';
  el.dataset.sidebar = look.sidebarStyle ?? 'light';
  el.dataset.density = look.density ?? 'comfortable';
}

/** Restore the last theme + look so the first paint already has the right colours. */
export function applyRememberedLook() {
  try {
    const t = localStorage.getItem(THEME_KEY) ?? 'system';
    const dark = t === 'dark' || (t === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    applyLook(JSON.parse(localStorage.getItem(LOOK_KEY) ?? '{}') as Look);
  } catch {
    /* ignore */
  }
}

/** Apply data-theme, accent, sidebar style, density + font scale on <html>; remembers them for a flash-free next load. */
export function useApplyTheme(theme: ThemeMode | undefined, fontScale: number | undefined, look?: Look) {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  useEffect(() => {
    let mode = theme;
    if (!mode) {
      try {
        mode = (localStorage.getItem(THEME_KEY) as ThemeMode | null) ?? 'system';
      } catch {
        mode = 'system';
      }
    } else {
      remember(THEME_KEY, mode);
    }
    const resolved = mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;
    document.documentElement.dataset.theme = resolved;
  }, [theme, prefersDark]);
  const { accent, sidebarStyle, density } = look ?? {};
  useEffect(() => {
    if (!look) return;
    applyLook({ accent, sidebarStyle, density });
    remember(LOOK_KEY, JSON.stringify({ accent, sidebarStyle, density }));
  }, [accent, sidebarStyle, density]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const s = Math.max(0.875, Math.min(1.375, fontScale ?? 1));
    document.documentElement.style.setProperty('--font-scale', String(s));
  }, [fontScale]);
}
